import { Routes } from '@/lib/routes';
import {
  IconArrowUp,
  IconLayoutGrid,
  IconPhoto,
  IconPhotoPlus,
  IconSparkles,
  IconVideo,
  IconWand,
  type Icon,
} from '@tabler/icons-react';

export interface ToolNavItem {
  key: string;
  label: string;
  description: string;
  href: string;
  icon: Icon;
  children?: ToolNavItem[];
}

export interface ToolNavGroup {
  key: string;
  label: string;
  href: string;
  icon: Icon;
  items: ToolNavItem[];
}

export const TOOL_NAV_GROUPS: ToolNavGroup[] = [
  {
    key: 'video-ai',
    label: 'Video AI',
    href: Routes.TextToVideo,
    icon: IconVideo,
    items: [
      {
        key: 'image-to-video',
        label: 'Image to Video',
        description: 'Animate a start frame into a Grok video draft.',
        href: Routes.ImageToVideo,
        icon: IconPhoto,
      },
      {
        key: 'photo-to-animation',
        label: 'Photo to Animation',
        description: 'Turn a still photo into a short animated clip.',
        href: Routes.PhotoToAnimation,
        icon: IconPhoto,
      },
      {
        key: 'reference-to-video',
        label: 'Reference to Video',
        description: 'Guide a Grok video with one to seven reference images.',
        href: Routes.ReferenceToVideo,
        icon: IconPhotoPlus,
      },
      {
        key: 'text-to-video',
        label: 'Text to Video',
        description: 'Generate a Grok video draft from a prompt.',
        href: Routes.TextToVideo,
        icon: IconVideo,
      },
      {
        key: 'video-editor',
        label: 'AI Video Editor',
        description: 'Edit a short video with a prompt and reference image.',
        href: Routes.AiVideoEditor,
        icon: IconWand,
      },
      {
        key: 'ai-video-extender',
        label: 'AI Video Extender',
        description:
          'Upload or choose a video and continue it by 6 or 10 seconds.',
        href: Routes.VideoExtend,
        icon: IconVideo,
      },
      {
        key: 'video-upscale',
        label: 'AI Video Upscaler',
        description: 'Upscale a short video at 2x or 4x with AI.',
        href: Routes.VideoUpscale,
        icon: IconArrowUp,
      },
    ],
  },
  {
    key: 'image-ai',
    label: 'Image AI',
    href: Routes.Generate,
    icon: IconPhoto,
    items: [
      {
        key: 'image-generator',
        label: 'text to image',
        description: 'Generate Grok AI images from a text prompt.',
        href: Routes.ImageGenerator,
        icon: IconPhoto,
      },
      {
        key: 'image-editor',
        label: 'AI image editor',
        description: 'Create and edit images with Grok Imagine models.',
        href: Routes.Generate,
        icon: IconSparkles,
      },
      {
        key: 'image-upscale',
        label: 'AI image upscaler',
        description: 'Upscale an image at 2x or 4x with AI.',
        href: Routes.ImageUpscale,
        icon: IconArrowUp,
      },
      {
        key: 'image-stitch',
        label: 'Image stitcher',
        description: 'Combine 2-9 photos in a local browser layout.',
        href: Routes.ImageStitch,
        icon: IconLayoutGrid,
      },
    ],
  },
];

export function flattenToolNavItems(items: ToolNavItem[]): ToolNavItem[] {
  return items.flatMap((item) => [
    item,
    ...flattenToolNavItems(item.children ?? []),
  ]);
}

export function isToolNavItemActive(item: ToolNavItem, pathname: string) {
  return flattenToolNavItems([item]).some(
    (candidate) => candidate.href === pathname
  );
}

export function isToolGroupActive(group: ToolNavGroup, pathname: string) {
  return flattenToolNavItems(group.items).some(
    (item) => item.href === pathname
  );
}
